import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Breaker,
  BreakerVertical,
  Button,
  Flexbox,
  InputField,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import {
  Calender,
  Clock,
  EyeOpen,
  FilledCircleCalender,
  Notification,
  Search,
  Users,
} from "../../Theme/Icons";
import InitialsAvatar from "../../Components/InitialsAvatar";
import {
  clearDashboardStates,
  dashboardData,
  getDashboardActivity,
  getDashboardStats,
} from "../../../redux/dashboard/dashboardSlice";
import { toast } from "react-toastify";
import { toastOpts } from "../../../utils/toastConfig";
import ModuleSwitcher, {
  useAvailableModules,
} from "../../Components/ModuleSwitcher";
import { MODULES, selectAppModule } from "../../../redux/appModule/appModuleSlice";

const pad2 = (n) => String(n ?? 0).padStart(2, "0");

const statusBadgeProps = (status) => {
  if (status === "Confirmed" || status === "Approved") return { green: true };
  if (status === "Rejected" || status === "Cancelled") return { red: true };
  return { yellow: true };
};

const formatBookingDate = (start) => {
  if (!start) return "";
  const d = moment.utc(start);
  if (d.isSame(moment.utc(), "day")) return "Today";
  if (d.isSame(moment.utc().add(1, "day"), "day")) return "Tomorrow";
  return d.format("MMM DD");
};

const formatBookingTime = (start, end) => {
  if (!start || !end) return "";
  return `${moment.utc(start).format("hh:mm")} - ${moment
    .utc(end)
    .format("hh:mm A")}`;
};

const DashboardAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    isStatsFetching,
    stats,
    isActivityFetching,
    upcomingBookings,
    recentActivities,
    isStatsError,
    statsErrorMessage,
    isActivityError,
    activityErrorMessage,
  } = useSelector(dashboardData);

  // The slice tracked both failures all along but nothing rendered them, so a
  // dead dashboard just showed "--" and empty lists with no explanation. One
  // toast covers both — a single outage shouldn't stack two notifications —
  // and clearing the state lets a later failure toast again.
  useEffect(() => {
    if (!isStatsError && !isActivityError) return;
    toast.error(
      statsErrorMessage || activityErrorMessage || "Couldn't load dashboard data. Please refresh.",
      toastOpts("dashboard-load-error")
    );
    dispatch(clearDashboardStates());
  }, [isStatsError, isActivityError, statsErrorMessage, activityErrorMessage, dispatch]);

  const { canSwitch: canSwitchModule } = useAvailableModules();
  const selectedModule = useSelector(selectAppModule);

  // Scope both the stat cards and the activity feed to the active module, so e.g.
  // the Bloomberg dashboard doesn't list CoWork / Makers Lab / Eduspace bookings.
  useEffect(() => {
    const moduleFlags = {
      isClassroom: selectedModule === MODULES.EDUSPACE,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    };
    dispatch(getDashboardStats(moduleFlags));
    dispatch(getDashboardActivity(moduleFlags));
  }, [dispatch, selectedModule]);

  const greeting = (() => {
    const hour = moment().hour();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  })();

  const statCards = [
    { label: "Total Rooms", value: stats?.totalRooms },
    { label: "Active Rooms", value: stats?.activeRooms },
    { label: "Today's Bookings", value: stats?.todaysBookings },
    { label: "Pending Requests", value: stats?.pendingRequests },
  ];

  return (
    <>
      <WrapperBox
        padding={"12px 20px"}
        background={globalTheme.colors.white1}
        boxShadow
        overflow={"visible"}
      >
        <Flexbox spaceBW alignCenter className="respToolbar">
          <Flexbox maxWidth="max-content">
            <ModuleSwitcher />
            {canSwitchModule && (
              <BreakerVertical
                marginLeft={16}
                marginRight={16}
                borderColor={globalTheme.colors.grey6}
              />
            )}
            <Flexbox column justiCenter maxWidth="max-content">
              <Text font16 inter semiBold black3>
                Dashboard
              </Text>
              <Text font14 inter regular grey2>
                {greeting} -- {moment().format("dddd, D MMMM YYYY")}
              </Text>
            </Flexbox>
          </Flexbox>
          <Flexbox maxWidth={"40%"} flexEnd gap={18} className="respToolbarGroup">
            <InputField
              noLabel
              hasIcon={<Search />}
              placeholder="Search rooms, bookings ..."
              revIcon
              maxWidth="300px"
              autoFocus
              onKeyDown={() => {}}
            />
            <WrapperBox
              padding={"9px"}
              borderColor={globalTheme.colors.nuetral2}
              borderRadius={30}
              maxWidth={"40px"}
              Height={"40px"}
            >
              <Notification color={globalTheme.colors.grey3} />
            </WrapperBox>
          </Flexbox>
        </Flexbox>
      </WrapperBox>

      <WrapperBox
        background={"transparent"}
        maxHeight={"calc(100% - 75px)"}
        className="respNoMaxH"
      >
        <Flexbox gap={12} alignStart className="respStatRow">
          {statCards.map((card) => (
            <WrapperBox
              key={card.label}
              borderColor={globalTheme.colors.nuetral2}
              borderRadius={20}
              maxWidth={"25%"}
              className="respStat4"
            >
              <Flexbox alignStart spaceBW>
                <Flexbox column gap={12} maxWidth="max-content">
                  <Text font16 semiBold inter nuetral13>
                    {card.label}
                  </Text>
                  <Text font20 semiBold inter black3>
                    {isStatsFetching ? "--" : pad2(card.value)}
                  </Text>
                </Flexbox>
                <FilledCircleCalender color={globalTheme.colors.yellow} />
              </Flexbox>
            </WrapperBox>
          ))}
        </Flexbox>

        <Flexbox alignStart gap={12} className="mt16 respPanelRow">
          <WrapperBox
            borderColor={globalTheme.colors.nuetral2}
            borderRadius={20}
            padding={"0"}
            className="respHalf"
          >
            <Flexbox spaceBW alignStart padding="20px 24px" className="respMetaRow">
              <Flexbox column gap={4} maxWidth="max-content">
                <Text font16 semiBold inter nuetral13>
                  Upcoming Bookings
                </Text>
                <Text font14 regular inter grey2>
                  Next 48 hours
                </Text>
              </Flexbox>
              <Button
                name="View All"
                buttonMd
                primaryOutlined
                icon={<EyeOpen width={"14px"} />}
                revIcon
                onClick={() => navigate("/user-bookings")}
              />
            </Flexbox>

            {isActivityFetching && (upcomingBookings || []).length === 0 ? (
              <Flexbox justiCenter alignCenter padding="40px 0">
                <Text font14 inter regular grey2>
                  Loading...
                </Text>
              </Flexbox>
            ) : (upcomingBookings || []).length === 0 ? (
              <Flexbox justiCenter alignCenter padding="40px 0">
                <Text font14 inter regular grey2>
                  No upcoming bookings
                </Text>
              </Flexbox>
            ) : (
              (upcomingBookings || []).map((booking) => (
                <React.Fragment key={booking?.id}>
                  <Breaker borderColor={globalTheme.colors.nuetral2} />
                  <Flexbox gap={12} alignCenter padding="20px 24px">
                    <InitialsAvatar
                      firstName={booking?.requestingUser?.firstName}
                      lastName={booking?.requestingUser?.lastName}
                    />
                    <Flexbox column gap={4}>
                      <Flexbox spaceBW alignCenter>
                        <Text font14 inter medium black3 className="respTruncate">
                          {booking?.room?.room_name || "—"}
                        </Text>
                        <Badge
                          {...statusBadgeProps(booking?.status)}
                          statusText={booking?.status || "—"}
                          small
                        />
                      </Flexbox>
                      <Flexbox spaceBW alignCenter className="respMetaRow">
                        <Text font14 inter regular nuetral6>
                          {booking?.createdAt
                            ? moment.utc(booking.createdAt).fromNow()
                            : ""}
                        </Text>
                        <Flexbox
                          gap={10}
                          alignCenter
                          flexEnd
                          className="respMetaRow"
                          maxWidth="max-content"
                        >
                          <Flexbox gap={4} alignCenter maxWidth="max-content">
                            <Calender color={globalTheme.colors.nuetral6} />
                            <Text font12 inter regular nuetral6>
                              {formatBookingDate(booking?.start_time)}
                            </Text>
                          </Flexbox>
                          <Flexbox gap={4} alignCenter maxWidth="max-content">
                            <Clock />
                            <Text font12 inter regular nuetral6>
                              {formatBookingTime(
                                booking?.start_time,
                                booking?.end_time
                              )}
                            </Text>
                          </Flexbox>
                          <Flexbox gap={4} alignCenter maxWidth="max-content">
                            <Users />
                            <Text font12 inter regular nuetral6>
                              {pad2((booking?.attendees || []).length)}
                            </Text>
                          </Flexbox>
                        </Flexbox>
                      </Flexbox>
                    </Flexbox>
                  </Flexbox>
                </React.Fragment>
              ))
            )}
          </WrapperBox>

          <WrapperBox
            borderColor={globalTheme.colors.nuetral2}
            borderRadius={20}
            padding={"0"}
            className="respHalf"
          >
            <Flexbox column gap={4} padding="20px 24px">
              <Text font16 semiBold inter nuetral13>
                Recent Activities
              </Text>
              <Text font14 regular inter grey2>
                System Events
              </Text>
            </Flexbox>

            {isActivityFetching && (recentActivities || []).length === 0 ? (
              <Flexbox justiCenter alignCenter padding="40px 0">
                <Text font14 inter regular grey2>
                  Loading...
                </Text>
              </Flexbox>
            ) : (recentActivities || []).length === 0 ? (
              <Flexbox justiCenter alignCenter padding="40px 0">
                <Text font14 inter regular grey2>
                  No recent activity
                </Text>
              </Flexbox>
            ) : (
              (recentActivities || []).map((activity) => (
                <React.Fragment key={activity?.id}>
                  <Breaker borderColor={globalTheme.colors.nuetral2} />
                  <Flexbox spaceBW alignCenter padding="20px 24px" className="respMetaRow">
                    <Flexbox gap={12} alignCenter maxWidth="max-content">
                      <FilledCircleCalender />
                      <Text font14 inter regular black3>
                        {activity?.message || activity?.description || "—"}
                      </Text>
                    </Flexbox>
                    <Text font11 inter medium nuetral6>
                      {activity?.createdAt
                        ? moment.utc(activity.createdAt).fromNow()
                        : ""}
                    </Text>
                  </Flexbox>
                </React.Fragment>
              ))
            )}
          </WrapperBox>
        </Flexbox>
      </WrapperBox>
    </>
  );
};

export default DashboardAdmin;
